import PlaceTypeModel             from '@models/map/PlaceTypeModel';
import Form                       from '@widesk-ui/components/form/Form';
import PlaceTypeFieldModel        from '@models/map/PlaceTypeFieldModel';
import View                       from '@widesk-ui/components/View';
import FieldModel                 from '@models/map/FieldModel';
import React                      from 'react';
import { FormTools }              from '@widesk-ui/components/form/Form';
import ColorPicker                from '@widesk-ui/components/form/ColorPicker';
import savePositionModelRelations from '@widesk-core/tools/savePositionModelRelations';
import BatchManager              from '@widesk-core/models/batch/BatchManager';
import { getIdFromUrn }            from '@widesk-core/tools/identifier';
import { observer }               from 'mobx-react-lite';
import useApiCollection            from '@widesk-core/hooks/useApiCollection';
import useDesignTokens             from '@/hooks/useDesignTokens';
import useForceUpdate               from '@widesk-core/hooks/useForceUpdate';
import Input                         from '@widesk-ui/components/form/Input';
import { pickFreeColor }             from '@/TokamapEditor/helpers/randomColor';
import SaveIndicator                from '@/TokamapEditor/ux/SaveIndicator';
import { SaveState }                from '@/TokamapEditor/ux/SaveIndicator';
import EditableText                  from '@widesk-ui/components/form/EditableText';
import Button                        from '@widesk-ui/components/Button';
import { PlusOutlined }              from '@ant-design/icons';
import { DeleteOutlined }            from '@ant-design/icons';
import { EnvironmentOutlined }       from '@ant-design/icons';
import leftSidebarStore              from '@/TokamapEditor/stores/leftSidebarStore';
import { message }                   from '@widesk-ui/hooks/useMessage';
import debounce                      from 'lodash/debounce';
import PagedCollection               from '@widesk-core/models/PagedCollection';
import PlaceModel                    from '@models/map/PlaceModel';
import FieldNaturePicker           from '@/forms/services/map/FieldNaturePicker';
import SelectTheme                  from '@selects/map/SelectTheme';
import { FormItem }                 from '@widesk-ui/components/form/FormItem';
import Field                        from '@/TokamapEditor/ux/AreaSidebar/Field';
import themeStore                   from '@/TokamapEditor/stores/themeStore';
import { proposeApplyTheme }        from '@/TokamapEditor/helpers/placeTypeThemeShapes';
import { proposeDetachTheme }       from '@/TokamapEditor/helpers/placeTypeThemeShapes';
import PlaceTypeFieldList           from '@/forms/services/map/PlaceTypeFieldList';
import messageWithUndo             from '@/TokamapEditor/helpers/messageWithUndo';
import PlacePanelPreview            from '@/TokamapEditor/ux/PlacePanelPreview';
import buildPlacePanelEntry         from '@/TokamapEditor/helpers/placePanelEntry';
import { sampleFieldValue }         from '@/TokamapEditor/helpers/placePanelEntry';
import placeTypeDeletionBlockedMessage from '@/forms/services/map/placeTypeDeletion';
import { functionalityStore }          from '@/components/Functionality';

/**
 * Le titre se modifie en place dans l'en-tête de la sidebar, comme le nom d'un thème (`ThemeTitle`) :
 * c'est la seule valeur du type qui soit aussi son identité, elle n'a donc pas sa place dans le corps
 * du formulaire, entre la couleur et la liste des champs.
 *
 * Exporté ici, et non dans la sidebar qui l'affiche, parce que le formulaire doit savoir le
 * reconstruire pour restaurer l'en-tête en sortant d'un sous-écran.
 */
export const PlaceTypeTitle = observer((props: {
	model: PlaceTypeModel;
	/** Enveloppe d'enregistrement du panneau : passée par `PlaceTypeForm`, elle fait apparaître le
	 * même "Enregistré" que pour la couleur ou les champs. Absente lorsque le titre est posé avant
	 * que le formulaire ne soit monté — le renommage marche alors, simplement sans indicateur. */
	onSave?: (task: () => Promise<void>) => Promise<void>;
}) => (
	<EditableText
		value={props.model.label}
		placeholder="Nom du type"
		errorMessage="Impossible de renommer ce type"
		onSubmit={async label => {
			// `EditableText` affiche le message de l'`Error` levée, sous le champ resté ouvert.
			if (!label) throw new Error('Le nom est obligatoire.');

			const task = async () => {
				await props.model.patch({ label });

				leftSidebarStore.getListStore('placeTypes')?.reload();
			};

			await (props.onSave ? props.onSave(task) : task());
		}}
	/>
));

export type PlaceTypeFormProps = {
	disableContinueAfterSubmit?: boolean;
	/** Rendu dans le second niveau de la sidebar : le formulaire peut alors y afficher ses
	 * sous-écrans en remplaçant son propre contenu, en-tête compris. Faux en modale. */
	inSidebar?: boolean;
	model: PlaceTypeModel;
	onSuccess?: () => void;
	projectUrn: Urn;
}

const linkedThemeUrn = (placeType: PlaceTypeModel) => placeType.getId('theme')
	? placeType.getUrn('theme')
	: undefined;

async function proposeThemeShapes(placeTypeId: id, previousThemeUrn: Urn | undefined, nextThemeUrn: Urn | undefined, projectUrn: Urn) {
	if (!themeStore.isLoaded) await themeStore.load();

	const projectId = getIdFromUrn(projectUrn);
	const next = nextThemeUrn && themeStore.getTheme(String(getIdFromUrn(nextThemeUrn)));
	const previous = previousThemeUrn && themeStore.getTheme(String(getIdFromUrn(previousThemeUrn)));

	if (next) return proposeApplyTheme(next, [placeTypeId], projectId);
	if (previous) return proposeDetachTheme(previous, [placeTypeId], projectId);
}

const PlaceTypeForm = observer(({ disableContinueAfterSubmit, inSidebar, model, onSuccess, projectUrn }: PlaceTypeFormProps) => {
	const formToolsRef = React.useRef<FormTools<any>>();
	const linkedTheme = React.useRef<{ urn?: Urn; }>();

	// Branché sur le sélecteur et non sur `onChange` du formulaire, qui se déclenche aussi champ vide,
	// sans geste de l'utilisateur : il y lisait un retrait et déliait le type à l'ouverture.
	const saveTheme = (next: Urn | undefined) => {
		if (!inSidebar || !model.id || !linkedTheme.current) return;

		const previous = linkedTheme.current.urn;

		if ((next || null) === (previous || null)) return;

		linkedTheme.current = { urn: next || undefined };

		// `null` pour délier : en merge-patch, `undefined` laisserait le lien en place.
		void runSave(async () => {
			await model.patch({ theme: next || null });

			leftSidebarStore.getListStore('placeTypes')?.reload();
		})
			.then(
				() => proposeThemeShapes(model.id, previous, next || undefined, projectUrn),
				() => {
					linkedTheme.current = { urn: previous };
				},
			)
			.catch(() => undefined);
	};

	const placeTypeFieldCollection = useApiCollection(PlaceTypeFieldModel);

	// Les lignes et l'aperçu se déduisent des valeurs du formulaire, qui ne sont pas observables :
	// c'est ce re-rendu qui les tient à jour. `updateOnChange` ne suffisait pas — il ne redessine que
	// le `Form` lui-même, dont les enfants gardent la même référence et ne sont donc pas revisités.
	const forceUpdate = useForceUpdate();
	const tokens = useDesignTokens();

	/**
	 * La couleur s'enregistre au fil de l'eau, comme le titre et les champs : dans la sidebar, cet
	 * écran n'a plus de bouton à valider. Débouncé, parce qu'un nuancier émet une valeur à chaque
	 * mouvement du curseur et qu'on n'a pas besoin d'une requête par teinte traversée.
	 */
	const patchColor = React.useMemo(() => debounce((color: string) => {
		void runSave(async () => {
			await model.patch({ color });

			leftSidebarStore.getListStore('placeTypes')?.reload();
		}).catch(() => undefined);
	}, 600), [model]);

	React.useEffect(() => () => patchColor.cancel(), [patchColor]);

	// Les champs du projet, pour résoudre le nom et la nature derrière chaque URN.
	const fieldCollection = useApiCollection(FieldModel);

	React.useEffect(() => {
		fieldCollection.setRequiredFilter('project', getIdFromUrn(projectUrn)).list();
	}, [projectUrn]);

	// Champs déjà connus sans dépendre de ce chargement : ceux rattachés au type (résolus par
	// `onDidMount`) et ceux que l'utilisateur vient de créer. Sans ce cache, une ligne restait un
	// sélecteur tant que la liste du projet n'était pas revenue — c'est-à-dire, à l'ouverture, tout
	// le temps que l'écran est réellement regardé.
	const knownFields = React.useRef(new Map<string, FieldModel>());

	// Le tableau des champs vit ici et non dans le formulaire antd : la liste est un composant à part
	// (glisser-déposer, lignes lisibles), et `onSubmit` le lit directement. Un aller-retour par le
	// store du formulaire n'apporterait rien qu'une source de désynchronisation.
	const [fieldUrns, setFieldUrns] = React.useState<Urn[]>([]);

	/**
	 * Nombre de points d'intérêts portant ce type.
	 *
	 * Une seule requête, et seulement `totalItems` : `itemsPerPage: 1` suffit à obtenir le compte sans
	 * rapatrier les lignes. C'est ce qui rend l'information abordable ici, alors qu'elle ne l'est pas
	 * dans la liste des types — il y faudrait une requête par ligne.
	 */
	// Profondeur de ce panneau dans la pile, capturée au montage : le formulaire n'est plus forcément
	// au niveau 1 (il s'ouvre aussi depuis un point d'intérêt, donc au 2), et `setLowerOption` visait
	// un panneau qui n'était pas le sien — d'où un en-tête qui n'apparaissait pas.
	const depth = React.useRef(leftSidebarStore.panels.length - 1).current;

	const [placeCount, setPlaceCount] = React.useState<number>();

	React.useEffect(() => {
		if (!model.id) return;

		const collection = new PagedCollection(PlaceModel);

		collection
			.setRequiredFilter('placeType', model.id)
			.setItemsPerPage(1)
			.list()
			.then(() => setPlaceCount(collection.total))
			.catch(err => console.error('Comptage des points d\'intérêts du type', err));
	}, [model.id]);

	const [saveState, setSaveState] = React.useState<SaveState>('idle');
	const savedTimeout = React.useRef<ReturnType<typeof setTimeout>>();

	/** Enveloppe commune aux écritures au fil de l'eau : couleur, ajout, retrait, ordre. */
	const runSave = async (task: () => Promise<void>) => {
		clearTimeout(savedTimeout.current);
		setSaveState('saving');

		try {
			await task();

			setSaveState('saved');
			savedTimeout.current = setTimeout(() => setSaveState('idle'), 2500);
		} catch (err) {
			setSaveState('error');
			message.exception(err);
			throw err;
		}
	};

	React.useEffect(() => () => clearTimeout(savedTimeout.current), []);

	React.useEffect(() => {
		if (!inSidebar) return;

		leftSidebarStore.setPanelOption(depth, 'renderTopRight', () => <SaveIndicator state={saveState} />);
	}, [saveState, inSidebar]);

	React.useEffect(() => {
		if (!inSidebar) return;

		leftSidebarStore.setPanelOption(depth, 'title', <PlaceTypeTitle model={model} onSave={runSave} />);

		// Suppression offerte là où l'on regarde le type, et pas seulement depuis la liste : l'objet
		// existe dès l'ouverture de cette fiche, il doit pouvoir s'en défaire au même endroit.
		const blocked = placeTypeDeletionBlockedMessage(placeCount, fieldUrns.length);

		leftSidebarStore.setPanelOption(depth, 'actions', [
			{
				icon: <DeleteOutlined />,
				label: `Supprimer ${PlaceTypeModel.localize('the', 'l')}`,
				danger: true,
				// L'action reste à sa place, grisée : la retirer aurait laissé croire qu'on ne peut jamais
				// supprimer un type. L'infobulle dit ce qui bloque et ce qu'il faut défaire d'abord —
				// l'erreur d'API n'aurait expliqué ni l'un ni l'autre.
				disabled: !!blocked,
				disabledMessage: blocked,
				confirm: `Voulez-vous supprimer "${model.label}" ? Cette action est irréversible.`,
				onClick: async () => {
					try {
						await model.destroy();

						leftSidebarStore.getListStore('placeTypes')?.reload();
						leftSidebarStore.pop();
					} catch (err) {
						message.exception(err);
					}
				},
			},
		]);
	}, [inSidebar, model, placeCount, fieldUrns]);

	/**
	 * Les liens du type, dans l'ordre voulu par l'utilisateur.
	 *
	 * Trié explicitement sur `position` : `listBy` ne pose aucun tri, donc l'API rend les liens dans
	 * son ordre à elle (l'id, en pratique). Les positions étaient donc correctement enregistrées, mais
	 * relues dans le désordre — d'où un ordre qui ne survivait pas à un rechargement.
	 */
	const orderedFieldUrns = () => [...placeTypeFieldCollection.models]
		.sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
		.map(ptf => ptf.getUrn('field'))
		.filter(Boolean) as Urn[];

	const resolveField = (urn?: Urn): FieldModel | undefined => {
		if (!urn) return undefined;

		return knownFields.current.get(urn) || fieldCollection.find(f => f.urn === urn) || undefined;
	};

	/**
	 * Applique une nouvelle composition de la fiche — ajout, retrait ou réordonnancement.
	 *
	 * Écrit tout de suite quand le type existe déjà : après un message "champ ajouté", personne ne
	 * s'attend à devoir encore enregistrer, et différer le rattachement donnait un écran où l'ajout
	 * semblait fait sans l'être. Retrait et ordre suivent la même règle, sans quoi l'écran serait à
	 * moitié différé — plus déroutant encore.
	 *
	 * Un type pas encore créé n'a pas d'URN à rattacher : dans ce seul cas, la composition attend
	 * l'enregistrement, qui crée le type puis ses liens.
	 */
	const commitFields = async (next: Urn[]) => {
		const previous = fieldUrns;

		// Optimiste : la liste bouge tout de suite, on revient en arrière si l'API refuse.
		setFieldUrns(next);

		if (!model.id) return;

		try {
			await runSave(async () => {
				await savePositionModelRelations({
				initialModels: placeTypeFieldCollection.models,
				modelProp: 'field',
				sequential: true,
				newUrns: next,
				modelClass: PlaceTypeFieldModel,
				addedAttributes: (urn, position) => ({ field: urn, placeType: model.urn, position }),
					removedId: urn => placeTypeFieldCollection.find(ptf => ptf.getUrn('field') === urn)?.id,
				});

				// Rechargé pour que le prochain appel diffe contre l'état réel, et non contre celui d'avant.
				await placeTypeFieldCollection.listBy([model.id!], 'placeType');
				await placeTypeFieldCollection.whenIsLoaded(m => m.field);

				// Réaligné sur ce que l'API a réellement retenu, plutôt que de faire confiance à l'état
				// optimiste : si une position n'était pas passée, ça se voit tout de suite.
				setFieldUrns(orderedFieldUrns());
			});
		} catch {
			// Déjà signalée par `runSave` : ici on ne fait que revenir à l'état précédent.
			setFieldUrns(previous);
		}
	};

	/**
	 * Crée un champ et le rattache au type, en UN lot transactionnel.
	 *
	 * Les deux écritures ne veulent rien dire l'une sans l'autre : un champ créé puis laissé sans
	 * rattachement est orphelin — rien ne l'utilise, et aucun écran ne permet de le rattacher après
	 * coup (voir `leftSidebarStore.openFields`). C'est pourtant ce que laissait derrière lui un échec
	 * du second appel, sans rien en dire de plus qu'un message d'erreur.
	 *
	 * Type pas encore créé : il n'y a pas d'URN à rattacher. Le champ part alors seul, et son lien
	 * attend l'enregistrement avec le reste de la composition (voir `commitFields`).
	 */
	const addNewField = async (attributes: { label: string; fieldType: Urn }) => {
		// Composition d'avant, pour pouvoir y revenir : l'annulation doit défaire l'ajout, pas
		// recalculer un état.
		const previous = fieldUrns;
		const batch = new BatchManager();

		const field = batch.create(FieldModel, { ...attributes, project: projectUrn });

		if (model.id) {
			batch.create(PlaceTypeFieldModel, {
				// Un jeton : l'API le remplace par l'URN du champ que le lot vient de créer.
				field: field.urn,
				placeType: model.urn,
				position: fieldUrns.length,
			});
		}

		try {
			await runSave(async () => {
				await batch.run();

				if (!model.id) return;

				// Relu pour que le prochain remaniement de la composition diffe contre l'état réel, lien
				// tout juste créé compris (voir `commitFields`).
				await placeTypeFieldCollection.listBy([model.id], 'placeType');
				await placeTypeFieldCollection.whenIsLoaded(m => m.field);
			});
		} catch {
			// Déjà signalée par `runSave` : le lot n'ayant rien écrit, il n'y a rien à défaire.
			return undefined;
		}

		knownFields.current.set(field.model.urn, field.model);
		setFieldUrns(model.id ? orderedFieldUrns() : [...fieldUrns, field.model.urn]);
		functionalityStore.refreshUsagesOf('place_field_limit');

		// Créer puis nommer laisse l'utilisateur sans bouton à ne pas valider : l'annulation est sa
		// seule porte de sortie, comme pour un type ou un point d'intérêt. Ici elle détache ET supprime
		// — le champ vient d'être créé, personne d'autre ne s'en sert. Le nom étant généré (voir
		// `FieldNaturePicker`), on le cite et on dit où le corriger : ce message est le seul émis pour
		// cette création.
		messageWithUndo(
			<span>« {attributes.label} » a été créé. Cliquez sur son nom pour le renommer.</span>,
			async () => {
				await commitFields(previous);
				await field.model.destroy();
				functionalityStore.refreshUsagesOf('place_field_limit');

				setFieldUrns(previous);
			},
		);

		return field.model;
	};

	const addField = (field: FieldModel) => {
		knownFields.current.set(field.urn, field);

		if (fieldUrns.includes(field.urn)) return;

		const previous = fieldUrns;

		void commitFields([...previous, field.urn]);

		// Détachement seul, sans suppression : ce champ existait avant, il appartient au projet et
		// d'autres types s'en servent peut-être.
		messageWithUndo(<span>« {field.label} » a été ajouté à ce type.</span>, async () => {
			await commitFields(previous);

			setFieldUrns(previous);
		});
	};

	/**
	 * Un panneau de plus sur la pile, au lieu de masquer celui-ci : le formulaire reste monté,
	 * l'en-tête et le retour se déduisent de la profondeur, et il n'y a plus de pied de tiroir à
	 * disputer.
	 */
	const openFieldPicker = () => leftSidebarStore.push({
		title: `Ajouter ${FieldModel.localize('a', 'l')}`,
		width: 360,
		closeButton: true,
		render: () => (
			<FieldNaturePicker
				projectUrn={projectUrn}
				usedUrns={fieldUrns}
				takenLabels={fieldCollection.map(f => f.label).filter(Boolean)}
				onCreate={addNewField}
				onReuse={addField}
				onDone={() => leftSidebarStore.pop()}
			/>
		),
	});

	return (
		<Form
			updateOnChange
			onChange={tools => {
				forceUpdate();

				if (!inSidebar || !model.id) return;

				const { color } = tools.getValues();

				if (color && color !== model.color) patchColor(color);
			}}
			onSuccess={onSuccess}
			onDidMount={async formTools => {
				formToolsRef.current = formTools;

				// Couleur pré-remplie à la création : elle est obligatoire, et la choisir avant même
				// d'avoir nommé le type n'apporte rien — la fiche permet d'en changer ensuite.
				if (!model.id) formTools.setFieldsValue({ color: pickFreeColor([]) } as any);

				if (model.id) {
					await placeTypeFieldCollection.listBy([model.id], 'placeType');
					await placeTypeFieldCollection.whenIsLoaded(m => m.field);

					placeTypeFieldCollection.forEach(ptf => {
						if (ptf.field?.urn) knownFields.current.set(ptf.field.urn, ptf.field);
					});

					setFieldUrns(orderedFieldUrns());

					linkedTheme.current = { urn: linkedThemeUrn(model) };

					formTools.setFieldsValue({
						label: model.label,
						color: model.color,
						theme: linkedTheme.current.urn,
					} as any);

					// `setFieldsValue` ne passe pas par `onChange` : sans ça, les champs déjà rattachés
					// resteraient invisibles jusqu'à la première modification.
					forceUpdate();
				}
			}}
			// Pas de champ "Titre" : il se modifie en place dans l'en-tête de la sidebar
			// (`PlaceTypeTitle`), comme le nom d'un thème. Le formulaire ne garde que ce qui n'a pas sa
			// place dans un en-tête — c'est-à-dire, désormais, la seule couleur.
			fields={{
				// Rétabli hors sidebar : là, le titre se modifie dans l'en-tête (`PlaceTypeTitle`), mais en
				// modale — la création depuis un `SelectPlaceType` — il n'y a pas d'en-tête à détourner, et
				// sans ce champ le type était impossible à nommer.
				label: {
					hidden: !!inSidebar,
					label: 'Titre du type',
					element: <Input placeholder="Titre" />,
					required: !inSidebar,
					span: 24,
				},
				color: inSidebar ? {
					noFormItem: true,
					element: (
						<Field label="Couleur">
							<FormItem name="color" required>
								<ColorPicker />
							</FormItem>
						</Field>
					),
					span: 24,
				} : {
					label: 'Couleur',
					element: <ColorPicker />,
					span: 24,
					required: true,
				},
				theme: inSidebar ? {
					noFormItem: true,
					element: (
						<Field label="Thème">
							<FormItem name="theme">
								<SelectTheme
									allowClear
									placeholder="Aucun thème"
									projectUrn={projectUrn}
									onChange={value => saveTheme(value as Urn | undefined)}
								/>
							</FormItem>
						</Field>
					),
					span: 24,
				} : {
					label: 'Thème',
					element: <SelectTheme allowClear placeholder="Aucun thème" projectUrn={projectUrn} />,
					span: 24,
				},
			}}
			// `false` dans la sidebar : c'est ce qui masque le pied du formulaire (voir `Form`), devenu
			// sans objet puisque titre, couleur et champs y partent seuls. En modale — la création d'un
			// type — le bouton reste indispensable.
			continueAfterSubmit={!inSidebar && !disableContinueAfterSubmit}
			// `false` uniquement dans la sidebar : `Form` y pose sinon un "Valider" dans le pied du
			// tiroir, quoi qu'on demande par ailleurs. `undefined` en modale, pour ne rien changer au
			// comportement par défaut de la création d'un type.
			withButton={inSidebar ? false : undefined}
			onSubmit={async values => {
				await model.patch({
					// `values.label` en modale, `model.label` en sidebar où le titre vit dans l'en-tête.
					label: values.label || model.label,
					color: values.color,
					theme: values.theme || null,
					project: projectUrn,
				});

				// Sans effet quand les liens ont déjà été écrits au fil de l'eau (le diff est alors vide) :
				// ne sert qu'au type tout juste créé, qui n'avait pas encore d'URN à rattacher.
				await savePositionModelRelations({
					initialModels: placeTypeFieldCollection.models,
					modelProp: 'field',
					sequential: true,
					newUrns: fieldUrns,
					modelClass: PlaceTypeFieldModel,
					addedAttributes: (urn, position) => ({
						field: urn, placeType: model.urn, position,
					}),
					removedId: urn => placeTypeFieldCollection.find(ptf => ptf.getUrn('field') === urn)?.id,
				});

				return model;
			}}
		>
			{/* Le titre dit ce que la liste PRODUIT, pas ce qu'elle contient : "Champs" nomme la
			    mécanique, "la fiche d'un ..." nomme le résultat, qui est ce que l'utilisateur cherche. */}
			{/* Présenté comme une information à part entière et non comme une note de bas de page : c'est
			    la portée de tout ce qu'on règle dans ce panneau. Même vocabulaire visuel que les lignes
			    de champs — carré teinté, valeur en gras, précision en dessous. */}
			{placeCount !== undefined && (
				<View row centerV gap="sm" p="xs" br={8} bg={tokens.surface.subtle}>
					<View
						w={28}
						h={28}
						br={7}
						center
						color={tokens.color.primary}
						bg={tokens.fill.subtle}
						style={{ flexShrink: 0 }}
					>
						<EnvironmentOutlined />
					</View>

					{/* Une ligne, et assez courte pour y tenir : le panneau fait 380px, dont 28 pour l'icône.
					    "…ne porte ce type" repassait à la ligne, et la coupure en deux lignes d'avant — le
					    compte en gras, le verbe en gris dessous — se lisait comme un titre suivi d'une
					    légende alors que la seconde moitié ne veut rien dire sans la première.

					    "Aucun" plutôt que "0" : c'est ce qu'on écrit, et ça évite l'accord bancal du zéro. */}
					{/* Tout dans UN `span` : `View` est un flex en colonne, et chaque élément enfant y devient
					    une ligne à lui. Le `<b>` du compte se retrouvait donc seul au-dessus du reste de la
					    phrase. Un enfant unique rend au texte son écoulement normal, coupures comprises. */}
					<View flex>
						<span>
							{placeCount === 0
								? <>Aucun {PlaceModel.localize('default', 'l')} de ce type</>
								: (
									<>
										<b>{placeCount}</b>
										{' '}
										{PlaceModel.localize(placeCount > 1 ? 'plural' : 'default', 'l')} de ce type
									</>
								)}
						</span>
					</View>
				</View>
			)}

			<View gap="sm">
				<View bold>{FieldModel.localize('plural')} de la fiche</View>

				<PlaceTypeFieldList
					fieldUrns={fieldUrns}
					resolveField={resolveField}
					onChange={next => void commitFields(next)}
					onSave={runSave}
				/>

				{/* Tant qu'il n'y a aucun champ, l'invitation EST l'état vide : le même bloc pointillé, en
				    plus haut, avec l'explication dedans. Trois blocs empilés — un titre, un constat de
				    vide, un bouton — pour dire une seule chose, c'était deux de trop.

				    Elle dit à quoi sert un champ parce que c'est ici qu'on en a le plus besoin : ce
				    panneau s'ouvre juste après la création d'un type, sur un écran encore vide. */}
				{fieldUrns.length ? (
					// Texte en couleur primaire : c'est l'action principale de l'écran, et un pointillé gris
					// se lisait comme une zone inerte plutôt que comme une invitation.
					<Button
						block
						type="dashed"
						icon={<PlusOutlined />}
						style={{ color: tokens.color.primary, borderColor: tokens.color.primary }}
						onClick={openFieldPicker}
					>
						Ajouter {FieldModel.localize('a', 'l')}
					</Button>
				) : (
					<View
						center
						gap={6}
						py="lg"
						px="md"
						br={8}
						pointer
						color={tokens.color.primary}
						hover={{ bg: tokens.surface.subtle }}
						style={{ border: `1px dashed ${tokens.color.primary}` }}
						onClick={openFieldPicker}
					>
						<View row centerV gap="xs" bold>
							<PlusOutlined />
							Ajouter {FieldModel.localize('a', 'l')}
						</View>

						<View size="small" center lineHeight={1.45} color={tokens.text.muted} style={{ maxWidth: 260 }}>
							Les {FieldModel.localize('plural', 'l')} sont ce qu'on renseignera sur chaque
							{' '}{PlaceModel.localize('default', 'l')} de ce type — horaires, téléphone, photo…
						</View>
					</View>
				)}
			</View>

			{/* Le même aperçu que sur la fiche d'un point d'intérêt, nourri de valeurs d'exemple : ici on
			    décide QUELS champs existent, sans qu'aucun point d'intérêt ne les ait encore remplis. Il
			    montre donc l'effet de la liste — son ordre, ce qu'un champ `title` remplace, et le fait
			    qu'un champ de nature Image ne s'affiche pas. */}
			<PlacePanelPreview
				entry={buildPlacePanelEntry({
					label: `Exemple de ${model.label || PlaceTypeModel.localize('default', 'l')}`,
					location: 'Niveau 2 · Allée B',
					fields: fieldUrns.map(urn => {
						const field = resolveField(urn);
						const fieldType = field?.fieldType?.reference;

						return {
							label: field?.label || '',
							reference: field?.reference,
							fieldType,
							value: sampleFieldValue(fieldType),
						};
					}),
				})}
			/>
		</Form>
	);
});

export default PlaceTypeForm;
